import type { ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Menu, ShoppingBag, X } from 'lucide-react'
import { useT } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { Switchers } from '@/components/generals/switchers'
import { useShop } from '@/components/providers/shop-provider'

export const Header = (): ReactNode => {
    const t = useT()
    const router = useRouter()
    const { count, setCartOpen } = useShop()
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const links = [
        { href: '/shop', label: t('navShop') },
        { href: '/custom-order', label: t('navCustom') },
        { href: '/about', label: t('navAbout') },
        { href: '/contact', label: t('navContact') }
    ]

    const isActive = (href: string) => router.pathname === href || (href === '/shop' && router.pathname.startsWith('/artworks'))

    return (
        <header id='site-header' className='sticky top-0 z-40 border-b-2 border-line bg-background/95 backdrop-blur'>
            <div className='mx-auto flex h-18 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6'>
                <Link href='/' className='rounded-lg' aria-label='DrawSword home'>
                    <Logo />
                </Link>

                <nav aria-label='Main' className='hidden items-center gap-1 lg:flex'>
                    {links.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className={cn('rounded-full px-4 py-2 text-sm font-semibold transition-colors hover:bg-sky', isActive(link.href) && 'bg-sun')}
                            aria-current={isActive(link.href) ? 'page' : undefined}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <Switchers className='hidden md:flex' />

                    <button
                        type='button'
                        onClick={() => setCartOpen(true)}
                        className='relative inline-flex size-11 items-center justify-center rounded-full border-2 border-line bg-pink shadow-pop transition-transform hover:-translate-y-0.5'
                        aria-label={`${t('openCart')} (${count})`}
                    >
                        <ShoppingBag className='size-5' />

                        {count > 0 && (
                            <span className='absolute -right-1.5 -top-1.5 flex min-w-6 items-center justify-center rounded-full border-2 border-line bg-sun px-1 text-xs font-bold'>
                                {count}
                            </span>
                        )}
                    </button>

                    <button
                        type='button'
                        onClick={() => setMenuOpen(open => !open)}
                        className='inline-flex size-11 items-center justify-center rounded-full border-2 border-line bg-card lg:hidden'
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        aria-label={menuOpen ? t('closeMenu') : t('openMenu')}
                    >
                        {menuOpen ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div id='mobile-menu' className='border-t-2 border-line bg-background lg:hidden'>
                    <nav aria-label='Mobile' className='mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4 sm:px-6'>
                        {links.map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn('rounded-xl px-4 py-3 text-lg font-semibold hover:bg-card', isActive(link.href) && 'bg-sun')}
                            >
                                {link.label}
                            </Link>
                        ))}

                        <Switchers className='mt-3 md:hidden' />
                    </nav>
                </div>
            )}
        </header>
    )
}
